import { Box, FormControlLabel, Radio, RadioGroup, Button, Tabs, Tab } from '@material-ui/core';
import { Canceler } from 'axios';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { IUserSegmentAll } from '.';
import useAlertDialog from '../../../hooks/useAlertDialog';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';

import {
  deleteUserSegment,
  deleteUserSegmentCuration,
  getCuratedUserSegmentsWithTypesAndPage,
  toggleActiveUserSegment,
  updateUserSegmentCuration,
} from '../../../services/api/Segment';
import { HomeType, ISegmentCurationStatus, ISegmentCurationUpdateRequestDto, IUserSegment, UserSegmentType } from '../../../services/types';
import { RouteNames } from '../../config';
import { AllList } from './AllList';
import { CurationScheduleCurationList } from './CurationScheduleCurationList';
import useStyles from './styles';

enum CurationType {
  Schedule = 'Schedule',
  Segments = 'Segments',
}
interface IParamProps {
  segmentId: string;
}
interface IProps {
  onClickRowItem: (segmentItem: IUserSegment) => void;
  onError: (error: Error) => void;
  onClickCuration: (segmentItem: IUserSegment, mode: 'create' | 'edit') => void;
}

export const CurationScheduleList = (props: IProps): JSX.Element => {
  const match = useRouteMatch<IParamProps>(`${RouteNames.UserSegment}/v/:segmentId`);
  const segmentId = match?.params?.segmentId;
  const initialSegmentIds = segmentId ? [Number(segmentId)] : [];

  const classes = useStyles();
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const alert = useAlertDialog();
  const confirm = useConfirmDialog();
  // states
  const [status, setStatus] = useState<ISegmentCurationStatus | 'all'>('all');
  const [page, setPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [rowsPerPage, setRowsPerPage] = useState<number>(20);

  const [curationType, setCurationType] = useState<CurationType>(CurationType.Schedule);
  const [homeType, setHomeType] = useState<HomeType>(HomeType.ALL);
  const [editingSegmentIds, setEditingSegmentIds] = useState<number[]>(initialSegmentIds);

  const [userSegmentAll, setUserSegmentAll] = useState<IUserSegmentAll[]>();

  const isUserSegmentAllLoaded = useRef<boolean>(false);

  // refs
  const allTypeCanceler = useRef<Canceler>(() => undefined);

  // APIs
  const fetchAllTypeData = useCallback(async () => {
    if (isUserSegmentAllLoaded.current) {
      backdrop.show();
    }

    allTypeCanceler.current();

    try {
      const [fetchPromise, cancel] = getCuratedUserSegmentsWithTypesAndPage<IUserSegmentAll>(homeType, isActive, page + 1, rowsPerPage);
      allTypeCanceler.current = cancel;

      const result = await fetchPromise;

      isUserSegmentAllLoaded.current = true;

      setUserSegmentAll(result.data.items);
      setTotalCount(result.data.totalCount || 0);
    } catch (e) {
      if (api.isCancel(e)) return;
    } finally {
      backdrop.hide();
    }
  }, [backdrop, isActive, page, rowsPerPage, homeType]);

  // handlers
  const handleUpdateCuration = (curationId: number, data: ISegmentCurationUpdateRequestDto) => {
    return updateUserSegmentCuration(curationId, data);
  };

  const handleDeleteCuration = (curationId: number) => {
    return deleteUserSegmentCuration(curationId);
  };

  const handleChangeIsActive = (active: boolean) => {
    setPage(0);
    setIsActive(active);
  };

  const handleError = useCallback(
    e => {
      snackbar.error(`An error has occurred. ${e.response ? e.response.data.message : e.message}`);
    },
    [snackbar],
  );

  const handleOnToggleStatus = async (seg: IUserSegmentAll, type: UserSegmentType | 'all') => {
    if (!seg.id) {
      return;
    }
    confirm.open({
      title: 'Status change',
      text: seg.isActive
        ? 'This will deactivate the segment. Are you sure you want to change the status of the segment?'
        : 'Are you sure you want to activate this segment?',
      onProceed: async () => {
        try {
          backdrop.show();
          const result = await toggleActiveUserSegment(seg.id!.toString(), seg.type);
          if (result.status === 200) {
            fetchAllTypeData();
          } else {
            handleError(new Error(result.statusText));
          }
        } catch (e) {
          if (e.response.data.message.indexOf('parent') >= 0) {
            alert.open({ title: 'Alert', text: 'You cannot deactivate a segment that is in a group segment.' });
          } else {
            handleError(e);
          }
        } finally {
          backdrop.hide();
        }
      },
    });
  };
  const handleListDelete = async (seg: IUserSegmentAll, type: UserSegmentType | 'all', idx: number) => {
    if (seg.type !== UserSegmentType.Group && seg.isActive) {
      return;
    }

    const onProceed = async () => {
      if (!seg.id) {
        return;
      }

      try {
        backdrop.show();
        const result = await deleteUserSegment(seg.id);

        if (result.status === 200) {
          fetchAllTypeData();
        } else {
          handleError(new Error(result.statusText));
        }
      } catch (e) {
        handleError(e);
      } finally {
        backdrop.hide();
      }
    };

    confirm.open({
      title: `Delete`,
      text: `Are you sure you want to delete this segment?`,
      onProceed,
    });
  };

  // watches
  useEffect(() => {
    // console.log(`Curation Type Changed: ${curationType}`);
    if (curationType === CurationType.Segments) {
      fetchAllTypeData();
    }
  }, [curationType, fetchAllTypeData]);

  return (
    <div className={classes.root}>
      <Box style={{ width: '100%', marginTop: '8px', marginBottom: '14px', paddingLeft: '4px' }}>
        <Box>
          <Button
            className={cx(classes.curationButton, { on: curationType === CurationType.Schedule })}
            onClick={() => setCurationType(CurationType.Schedule)}
          >{`Curation ${CurationType.Schedule}`}</Button>
          <Button
            className={cx(classes.curationButton, { on: curationType === CurationType.Segments })}
            onClick={() => setCurationType(CurationType.Segments)}
          >{`Curation ${CurationType.Segments}`}</Button>

          <Tabs value={homeType} onChange={(_, value) => setHomeType(value)}>
            <Tab label={`Default Home`} value={HomeType.ALL} />
            <Tab label={`Read Home`} value={HomeType.READ} />
            <Tab label={`Listen Home`} value={HomeType.LISTEN} />
          </Tabs>
        </Box>

        <Box paddingBottom={1}>
          {curationType === CurationType.Schedule && (
            <RadioGroup
              row
              value={status}
              name="status"
              onChange={e => {
                setStatus(e.target.value as ISegmentCurationStatus);
              }}
            >
              <FormControlLabel control={<Radio value="all" color="primary" className={''} />} label="All status" />
              <FormControlLabel control={<Radio value="activated" color="primary" />} label="Activated" />
              <FormControlLabel control={<Radio value="scheduled" color="primary" />} label="Scheduled" />
              <FormControlLabel control={<Radio value="completed" color="primary" />} label="Completed" />
            </RadioGroup>
          )}
        </Box>
        {curationType === CurationType.Schedule ? (
          <>
            <Box marginBottom={'20px'}>
              <CurationScheduleCurationList
                homeType={homeType}
                type="featuredcard"
                status={status}
                onClickRowItem={props.onClickRowItem}
                onDeleteCuration={handleDeleteCuration}
                onUpdateCuration={handleUpdateCuration}
                onError={props.onError}
              />
            </Box>
            <Box>
              <CurationScheduleCurationList
                homeType={homeType}
                type="collection"
                status={status}
                onClickRowItem={props.onClickRowItem}
                onDeleteCuration={handleDeleteCuration}
                onUpdateCuration={handleUpdateCuration}
                onError={props.onError}
              />
            </Box>
          </>
        ) : (
          <>
            <AllList
              userSegmentAll={userSegmentAll}
              page={page}
              totalCount={totalCount}
              isActive={isActive}
              rowsPerPage={rowsPerPage}
              onClickRowItem={props.onClickRowItem}
              onError={handleError}
              onClickCuration={props.onClickCuration}
              setIsActive={handleChangeIsActive}
              fetchAllTypeData={fetchAllTypeData}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
              onToggleStatus={handleOnToggleStatus}
              onDelete={handleListDelete}
            />
          </>
        )}
      </Box>
    </div>
  );
};
